import {createBrowserRouter, Navigate, RouteObject} from "react-router";

import {RedirectWithParam} from "@/components/router/Redirect.tsx";
import {UserProfilePage} from "@/pages/accounts/userProfilePage/UserProfilePage.tsx";
import {UsersPage} from "@/pages/accounts/UsersPage.tsx";
import {AlertsPage} from "@/pages/alerting/AlertsPage.tsx";
import {AnalyzerPage} from "@/pages/analyzer/AnalyzerPage.tsx";
import {AuditLog} from "@/pages/auditlog/list/AuditLog.tsx";
import {RegisterPage} from "@/pages/auth/register/RegisterPage.tsx";
import {BillingPage} from "@/pages/billing/BillingPage.tsx";
import {CancelSubscriptionDone} from "@/pages/billing/cancel-subscription/CancelSubscriptionDone.tsx";
import {CancelSubscriptionPage} from "@/pages/billing/cancel-subscription/CancelSubscriptionPage.tsx";
import {CheckoutPage} from "@/pages/billing/checkout/CheckoutPage.tsx";
import {ConfirmationPage} from "@/pages/billing/confirmation/ConfirmationPage.tsx";
import {CustomQuoteRequestPage} from "@/pages/billing/custom-quote-request/CustomQuoteRequestPage.tsx";
import {InvoiceDetailsPage} from "@/pages/billing/invoices/InvoiceDetailsPage.tsx";
import {CheckAnalysis} from "@/pages/checks/analysis/CheckAnalysis.tsx";
import {Checks} from "@/pages/checks/list/Checks.tsx";
import {RTAPage} from "@/pages/checks/rta/RTAPage.tsx";
import {CreateCheckPage} from "@/pages/checks/update/CreateCheckPage.tsx";
import {EditCheckPage} from "@/pages/checks/update/EditCheckPage.tsx";
import {ContactsPage} from "@/pages/contacts/list/ContactsPage.tsx";
import {DashboardPage} from "@/pages/dashboard/display/DashboardPage.tsx";
import {DashboardPageNew} from "@/pages/dashboard/display/DashboardPageNew.tsx";
import {DashboardRoot} from "@/pages/dashboard/display/DashboardRoot.tsx";
import {CreateIncidentPage} from "@/pages/incidents/CreateIncidentPage.tsx";
import {EditIncidentPage} from "@/pages/incidents/EditIncidentPage.tsx";
import {IncidentsPage} from "@/pages/incidents/IncidentsPage.tsx";
import {IntegrationsPage} from "@/pages/integrations/IntegrationsPage.tsx";
import {OAuthCallbackPage} from "@/pages/integrations/OAuthCallbackPage.tsx";
import {Launchpad} from "@/pages/launchpad/Launchpad.tsx";
import {CreateMaintenanceSchedulePage} from "@/pages/maintenance/maintenance-create/CreateMaintenanceSchedulePage.tsx";
import {MaintenanceDashboardPage} from "@/pages/maintenance/maintenance-dashboard/MaintenanceDashboardPage.tsx";
import {EditMaintenanceSchedulePage} from "@/pages/maintenance/maintenance-edit/EditMaintenanceSchedulePage.tsx";
import {MetricsExplorerPage} from "@/pages/metricsExplorer/MetricsExplorerPage.tsx";
import {MobileAppsPage} from "@/pages/mobileapps/MobileAppsPage.tsx";
import {MonitoringServersPage} from "@/pages/monitoring/MonitoringServersPage.tsx";
import {PageSpeedDetails} from "@/pages/pagespeed/PageSpeedDetails.tsx";
import {ScheduledReports} from "@/pages/reports/schedule/ScheduledReports.tsx";
import {SLAReportDetails} from "@/pages/reports/sla/details/SLAReportDetails.tsx";
import {SLAReports} from "@/pages/reports/sla/SLAReports.tsx";
import {RUMCheckDetails} from "@/pages/rum/v2/details/RUMCheckDetails.tsx";
import {RUMPage} from "@/pages/rum/v2/RUMPage.tsx";
import {ListAllStatusPages} from "@/pages/statuspages/list/ListAllStatusPages.tsx";
import {CreateStatuspagePage} from "@/pages/statuspages/statuspages-create/CreateStatuspagePage.tsx";
import {ManageStatuspagePage} from "@/pages/statuspages/statuspages-manage/ManageStatuspagePage.tsx";
import {TagsPage} from "@/pages/tags/list/TagsPage.tsx";
import {UptimeWidget} from "@/pages/tools/UptimeWidget.tsx";
import {AccessDeniedPage} from "@/pages/website/AccessDeniedPage.tsx";
import {ContactPage} from "@/pages/website/contact/ContactPage.tsx";
import {FeatureUnavailablePage} from "@/pages/website/FeatureUnavailablePage.tsx";
import {NotFoundPage} from "@/pages/website/NotFoundPage.tsx";

import {Layout} from "../Layout.tsx";
import AccountDetailsPage from "../pages/accounts/account-details/AccountDetailsPage.tsx";
import {APITokensPage} from "../pages/apitokens/APITokensPage.tsx";
import {OnboardingWizardPage} from "../pages/onboardingwizard/OnboardingWizardPage.tsx";
import {SSOPage} from "../pages/sso/SSOPage.tsx";
import StatusPageConsumption from "../pages/statuspages/consumption/StatusPageConsumption.tsx";
import {VaultPage} from "../pages/vault/VaultPage.tsx";
import ProtectedEmptyLayout from "../ProtectedEmptyLayout.tsx";

const statusPagesRoutes: RouteObject[] = [
  {
    index: true,
    element: <ListAllStatusPages />,
  },
  {
    path: "all",
    element: <ListAllStatusPages />,
  },
  {
    path: "/statuspages/create",
    element: <CreateStatuspagePage />,
  },
  {
    path: ":id",
    children: [
      {
        index: true,
        element: <ManageStatuspagePage />,
      },
    ],
  },
];

const extraPages: RouteObject[] = [];
const extraProtectedEmptyPages: RouteObject[] = [
  {
    path: "/status-page/private/:id",
    element: <StatusPageConsumption mode="private" />,
  },
  {
    path: "/status-page/private/:id/:serviceId",
    lazy: async () => {
      const {CheckDrilldown} = await import("../pages/statuspages/consumption/pages/CheckDrilldown.tsx");
      return {element: <CheckDrilldown mode="private" />};
    },
  },
  {
    path: "/status-page/:slug",
    element: <StatusPageConsumption />,
  },
  {
    path: "/status-page/:slug/:serviceId",
    lazy: async () => {
      const {CheckDrilldown} = await import("../pages/statuspages/consumption/pages/CheckDrilldown.tsx");
      return {element: <CheckDrilldown />};
    },
  },
];

// https://vitejs.dev/guide/env-and-mode#node-env-and-modes
if (import.meta.env.VITE_ENVIRONMENT !== "production") {
  // add any routes that should be available everywhere except production
  // extraPages.push({
  //   path: "/example/path",
  //   element: <ExampleComponent />,
  // });

  // import.meta.env.DEV means local node.js server running
  if (import.meta.env.DEV) {
    // no WIP yet
  }
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Layout />,
    handle: {path: "/"},
    children: [
      {index: true, element: <Navigate to="/dashboard" replace />},
      {
        path: "*",
        element: <NotFoundPage />,
      },
      {
        path: "not-found",
        element: <NotFoundPage />,
      },
      {path: "access-denied", element: <AccessDeniedPage />},
      {path: "feature-unavailable", element: <FeatureUnavailablePage />},
      {
        path: "dashboard/",
        element: <DashboardRoot />,
      },
      {
        path: "dashboard/new",
        element: <DashboardPageNew />,
      },
      {
        path: "dashboard/:id",
        element: <DashboardPage />,
      },
      {
        path: "maintenance",
        children: [
          {
            index: true,
            element: <MaintenanceDashboardPage />,
          },
          {
            path: "schedules/create",
            element: <CreateMaintenanceSchedulePage />,
          },
          {
            path: "schedules/:id/edit",
            element: <EditMaintenanceSchedulePage />,
          },
        ],
      },
      {
        path: "contact-groups",
        element: <ContactsPage />,
      },
      {
        path: "checks",
        children: [
          {index: true, element: <Checks />},
          {path: "group", children: [{path: ":groupId", children: [{index: true, element: <Checks />}]}]},
          {path: "widget", element: <Navigate to="/widget" replace />},
          {path: "create", element: <CreateCheckPage />},
          {
            path: ":id",
            children: [
              {
                index: true,
                element: <CheckAnalysis />,
              },
              {path: "edit", element: <EditCheckPage />},
              {path: "analysis", element: <RTAPage />},
            ],
          },
        ],
      },
      {
        path: "metrics-explorer",
        element: <MetricsExplorerPage />,
      },
      {
        path: "assistant",
        children: [{path: "checks/create", element: <AnalyzerPage />}],
      },
      {
        // LEGACY ROUTING, DO NOT DELETE
        path: "devices",
        children: [
          {
            index: true,
            element: <Navigate to="/checks" replace />,
          },
          {
            path: "contact-groups",
            element: <Navigate to="/contact-groups" replace />,
          },
          {
            path: "services",
            children: [
              {index: true, element: <Navigate to="/checks" replace />},
              {path: "group", children: [{path: ":groupId", children: [{index: true, element: <Checks />}]}]},
              {path: "bulk-import", element: <Navigate to="/checks/bulk-import" replace />},
              {path: "widget", element: <Navigate to="/widget" replace />},
              {path: "create", element: <CreateCheckPage />},
              {
                path: ":id",
                children: [
                  {
                    index: true,
                    element: <RedirectWithParam urlPattern="/checks/:id" paramName="id" />,
                  },
                  {path: "edit", element: <EditCheckPage />},
                  {path: "analysis", element: <RedirectWithParam urlPattern="/checks/:id/analysis" paramName="id" />},
                ],
              },
            ],
          },
        ],
      },
      {
        path: "launchpad",
        element: <Launchpad />,
      },
      {
        path: "alerts",
        element: <AlertsPage />,
      },
      {
        // LEGACY ROUTING, DO NOT DELETE
        path: "alerting/alert-history",
        element: <Navigate to="/alerts" replace />,
      },
      {
        path: "alerting/alert-history/:id",
        element: <RedirectWithParam urlPattern="/alerts?pop=:id" paramName="id" />,
      },
      {
        path: "pagespeed/report/:id",
        element: <PageSpeedDetails />,
      },
      {
        path: "statuspages",
        children: statusPagesRoutes,
      },
      {
        path: "incidents",
        children: [
          {
            index: true,
            element: <IncidentsPage />,
          },
          {
            path: "create",
            element: <CreateIncidentPage />,
          },
          {
            path: ":id/edit",
            element: <EditIncidentPage />,
          },
        ],
      },
      {
        path: "rum",
        children: [
          {index: true, element: <Navigate to="/rum/v2" replace />},
          {
            path: "v2",
            children: [
              {
                index: true,
                element: <RUMPage />,
              },
              {
                path: "detail",
                children: [
                  {index: true, element: <Navigate to="/rum/v2" replace />},
                  {
                    path: ":id",
                    element: <RUMCheckDetails />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "billing",
        children: [
          {
            index: true,
            element: <BillingPage />,
          },
          {
            path: "subscription-summary",
            element: <Navigate to="/billing?action=subscription-summary" replace={true} />,
          },
          {
            path: "account-usage",
            element: <Navigate to="/billing?action=account-usage" replace={true} />,
          },
          {
            path: "subscribe",
            element: <Navigate to="/billing?action=subscribe" replace={true} />,
          },
          {
            path: "manage-subscription",
            element: <Navigate to="/billing?action=manage-subscription" replace={true} />,
          },
          {
            path: "checkout",
            element: <CheckoutPage />,
          },
          {
            path: "subscription/payment-method",
            element: <Navigate to="/billing?action=payment-method" replace={true} />,
          },
          {
            path: "subscription/done",
            element: <ConfirmationPage />,
          },
          {
            path: "custom-quote",
            element: <CustomQuoteRequestPage />,
          },
          {
            path: "manage-add-ons",
            element: <Navigate to="/billing?action=manage-add-ons" replace={true} />,
          },
          {
            path: "cancel",
            element: <CancelSubscriptionPage />,
          },
          {
            path: "cancel/done",
            element: <CancelSubscriptionDone />,
          },
          {
            path: "history",
            children: [
              {
                index: true,
                element: <Navigate to="/billing?action=invoices" replace={true} />,
              },
              {
                path: ":transactionId",
                element: <InvoiceDetailsPage />,
              },
            ],
          },
        ],
      },
      {
        path: "monitoring",
        children: [
          {
            index: true,
            element: <MonitoringServersPage />,
          },
          {path: "servers", element: <Navigate to="/monitoring" replace />},
        ],
      },
      {
        children: [
          {
            path: "mobile-apps",
            element: <MobileAppsPage />,
          },
          {
            path: "widget",
            element: <UptimeWidget />,
          },
        ],
      },
      {
        children: [
          {
            path: "accounts",
            children: [
              {
                index: true,
                element: <Navigate to="/accounts/account-details" replace />,
              },
              {
                path: "account-usage",
                element: <Navigate to="/billing/account-usage" replace />,
              },
              {
                path: "account-details",
                element: <AccountDetailsPage />,
              },
              {
                path: "user-management",
                element: <UsersPage />,
              },
              {
                path: "profile",
                element: <UserProfilePage />,
              },
              {
                path: "single-sign-on",
                element: <SSOPage />,
              },
            ],
          },
          {
            path: "api/tokens",
            element: <APITokensPage />,
          },
          {
            path: "audit/log",
            element: <AuditLog />,
          },
          {
            path: "tags",
            element: <TagsPage />,
          },
          {
            path: "vault",
            element: <VaultPage />,
          },
        ],
      },
      {
        path: "integrations",
        children: [
          {
            index: true,
            element: <Navigate to="/integrations/manage" replace />,
          },
          {
            path: "manage",
            element: <IntegrationsPage />,
          },
          {
            path: "oauth-callback",
            element: <OAuthCallbackPage />,
          },
        ],
      },
      {
        path: "reports",
        children: [
          {index: true, element: <Navigate to="/reports/sla" replace />},
          {
            path: "sla",
            element: <SLAReports />,
          },
          {
            path: "scheduled",
            element: <ScheduledReports />,
          },
          {
            path: "sla/:id",
            element: <SLAReportDetails />,
          },
        ],
      },
      {
        path: "contact",
        element: <ContactPage />,
      },
      ...extraPages,
    ],
  },
  {
    element: <ProtectedEmptyLayout />,
    children: [
      {
        path: "/onboarding",
        element: <OnboardingWizardPage />,
      },
      ...extraProtectedEmptyPages,
    ],
  },
  {
    path: "/checkout",
    element: <RegisterPage />,
  },
];

export const Router = createBrowserRouter(routes);
